<template>
	<AppLayout :title="trans('admin.users.rotations.history')">
		<template #actions>
			<Link v-if="unresolvedRequest && unresolvedRequest.status === 'reschedule'" :as="PrimaryButton" :color="Color.Purple" :href="route('association.representative-rotations.requests.create')">
				<i class="fa-solid fa-calendar-day mr-2"/>
				{{ trans('admin.users.rotations.requests.unresolved.reschedule') }}
			</Link>
			<Badge v-else-if="unresolvedRequest" :color="Color.Amber">
				<i class="fa-solid fa-question-circle mr-2"/>
				{{ trans('admin.users.rotations.requests.unresolved.pending') }}
			</Badge>
		</template>
		<Empty v-if="rotations.length === 0"/>
		<CardGrid v-else>
			<Card v-for="rotation in rotations" :title="rotation.college.acronym" :subtitle="rotation.college.name" no-grow>
				<template #badges>
					<Badge :color="rotation.colors" active>{{ trans(`admin.users.rotations.status.${rotation.status}`) }}</Badge>
					<i v-if="rotation.online" class="fa-solid fa-video-camera text-gray-400"/>
				</template>
				<LabeledObject>
					<template #label>{{ trans('admin.audit.elections.admissions.rotation_date') }}</template>
					<p>{{ dayjs(rotation.election_begins_at).format(trans('general.dayjs.datetime')) }} - {{ dayjs(rotation.election_ends_at).format(trans('general.dayjs.time')) }}</p>
				</LabeledObject>
				<List v-if="rotation.user_is_candidate || rotation.user_is_voter">
					<ListItem v-if="rotation.user_is_voter" :title="trans('admin.users.rotations.voting')">
						<Badge :color="rotation.voter.colors" active>{{ trans(`admin.audit.rotations.voters.status.${rotation.voter.status}`) }}</Badge>
					</ListItem>
					<ListItem v-if="rotation.user_is_candidate" :title="trans('admin.users.rotations.candidacy')">
						<Badge :color="rotation.candidate.colors" active>{{ trans(`admin.audit.rotations.candidates.status.${rotation.candidate.status}`) }}</Badge>
					</ListItem>
				</List>
				<Link v-if="rotation.user_is_voter && rotation.is_confirmed" :as="CardButton" :href="route('association.representative-rotations.elections.index', rotation.id)">
					{{ trans('admin.audit.elections.admissions.vote.title') }}
				</Link>
				<Link v-if="rotation.user_is_elected_candidate || rotation.user_is_admitted_candidate" :as="CardButton" :href="route('association.representative-rotations.history.compromise-letters.index', rotation.candidate.id)">
					{{ trans('admin.audit.rotations.documentation.compromise_letters.title') }}
				</Link>
				<Link v-if="rotation.user_is_outgoing_representative && (rotation.is_confirmed || rotation.is_admissible || rotation.is_completed)" :as="CardButton" :href="route('association.representative-rotations.history.meeting-letters.index', rotation.id)">
					{{ trans('admin.audit.rotations.documentation.meeting_letters.title') }}
				</Link>
			</Card>
		</CardGrid>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {defineProps} from 'vue'
import {Link} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/Empty.vue'
import Card from '@/Components/Cards/Card.vue'
import List from '@/Components/Lists/List.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Badge from '@/Components/Badges/Badge.vue'
import CardGrid from '@/Components/Cards/CardGrid.vue'
import ListItem from '@/Components/Lists/ListItem.vue'
import CardButton from '@/Components/Cards/CardButton.vue'
import LabeledObject from '@/Components/LabeledObject.vue'
import type Rotation from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import type RotationRequest from '@/Types/Periods/Rotations/RotationRequest'

defineProps<{
	rotations:Array<Rotation>,
	unresolvedRequest:RotationRequest|null,
}>()
</script>